import { useMemo, useState } from "react";
import { Box, Typography, CircularProgress, Grid, Checkbox } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import style from "./speciality.module.css";
import {
  useGetSpecialityDetailQuery,
  useGetClinicalDiagramsQuery,
  useUpdateSpecialityDetailsMutation,
} from "../../services/api";
import { Button, LabelBox } from "../../components";
import { ConfirmationDialog } from "../../components/Dialog";
import { colors } from "../../theme/colors";

/**
 * Speciality details view with clinical diagram and deactivation info.
 *
 * Notes:
 * - Displays clinical diagram names based on ids in the speciality detail.
 * - TODO: Confirm desired empty/error state behavior when detail fetch fails.
 */
const SpecialityDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [activateModalVisible, setActivateModalVisible] = useState(false);
    const [isActivating, setIsActivating] = useState(false);
    const [message, setMessage] = useState<LabelMessageType | null>(null);

    const {
        data: specialityDetails,
        isLoading,
        isFetching,
    } = useGetSpecialityDetailQuery(id ?? "");

    const { data: clinicalDiagramData, isLoading: isClinicalDiagramLoading } = useGetClinicalDiagramsQuery({
        order_by_columns: "name",
        order: "Asc",
        is_detailed: false,
        search_key: ""
    });
    const [updateSpecialityDetails] = useUpdateSpecialityDetailsMutation();

    const handleActivate = async () => {
        if (!specialityDetails?.id) {
            return;
        }
        try {
            setIsActivating(true);
            await updateSpecialityDetails({
                is_active: true,
                id: specialityDetails.id,
                deactivate_reason: "",
            }).unwrap();
            setActivateModalVisible(false);
        } catch (error: any) {
            setActivateModalVisible(false);
            setMessage({
                type: "error",
                text: error?.message || error?.data?.detail || "Something went wrong!",
            });
        } finally {
            setIsActivating(false);
        }
    };

    const clinicalDiagrams = useMemo<ClinicalDiagram[]>(
        () =>
            // Translate stored ids into display-ready records; missing ids are ignored.
            specialityDetails?.clinical_diagram_ids
                ?.map((diagramId) =>
                    clinicalDiagramData?.items?.find(
                        (clinicalDiagram: ClinicalDiagram) =>
                            clinicalDiagram.id === diagramId
                    )
                )
                .filter(
                    (diagram): diagram is ClinicalDiagram => Boolean(diagram)
                ) ?? [],
        [clinicalDiagramData, specialityDetails]
    );

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "30px",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    border: `1px solid ${colors.grey}`,
                    borderRadius: "4px",
                    height: "100%",
                }}
            >
                {isLoading || isFetching || isClinicalDiagramLoading ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "calc(100vh - 97px)",
                        }}
                    >
                        <CircularProgress size={40} sx={{ marginLeft: "10px" }} />
                    </Box>
                ) : (
                    <Box sx={{ height: "100%", padding: "15px 40px" }}>
                        <Typography className={style.specialityDetailHeading}>
                            Speciality Details
                        </Typography>
                        <Box>
                            <Box className={style.detailsRow}>
                                <Typography className={style.title}>Name</Typography>
                                <Typography className={style.value}>
                                    {specialityDetails?.name}
                                </Typography>
                            </Box>
                            <Box
                                className={style.detailsRow}
                                sx={{ alignItems: "flex-start !important" }}
                            >
                                <Typography className={style.title}>Clinical Diagrams</Typography>
                                <Box className={style.value}>
                                    <Grid container columnSpacing={3}>
                                        {clinicalDiagrams?.map((clinicalDiagram: ClinicalDiagram) => {
                                            return (
                                                <Grid
                                                    item
                                                    xs={clinicalDiagrams?.length > 10 ? 6 : 12}
                                                    key={clinicalDiagram?.id}
                                                >
                                                    <Box
                                                        display={"flex"}
                                                        alignItems={"center"}
                                                        paddingY={1}
                                                    >
                                                        <Checkbox
                                                            checked={true}
                                                            disableTouchRipple
                                                            sx={{
                                                                transform: "scale(0.92)",
                                                                padding: "0px",
                                                            }}
                                                            disabled
                                                        />
                                                        <Typography sx={{ marginLeft: 1 }}>
                                                            {clinicalDiagram?.name ?? ""}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </Box>
                            </Box>
                        </Box>
                        {specialityDetails?.deactivate_reason && (
                            <Box>
                                <Typography className={style.specialityDetailHeading}>
                                    Deactivation Details
                                </Typography>
                                <Box>
                                    <Box className={style.detailsRow}>
                                        <Typography className={style.title}>
                                            Reason for deactivation
                                        </Typography>
                                        <Typography className={style.value}>
                                            {specialityDetails?.deactivate_reason}
                                        </Typography>
                                    </Box>
                                    <Box className={style.detailsRow}>
                                        <Typography className={style.title}>
                                            Date of Deactivation
                                        </Typography>
                                        <Typography className={style.value}>
                                            {specialityDetails?.deactivated_at
                                                ? dayjs(specialityDetails?.deactivated_at).format(
                                                    "MMMM DD YYYY, hh:mm:ss A"
                                                )
                                                : "-"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        )}
                        {message && (
                            <Box mt={2}>
                                <LabelBox
                                    boxProps={{
                                        height: 50,
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                    data={message}
                                    onClose={() => setMessage(null)}
                                />
                            </Box>
                        )}
                        <Box className={style.buttons} sx={{ marginBottom: "20px" }}>
                            <Button
                                sx={{
                                    width: "200px",
                                    height: "55px",
                                    ":hover": { backgroundColor: "transparent" },
                                }}
                                variant="outlined"
                                onClick={() => navigate(-1)}
                                label={"Close"}
                            />
                            {specialityDetails?.is_active ? (
                                <Button
                                    sx={{
                                        width: "200px",
                                        height: "55px",
                                        ":hover": { backgroundColor: colors.primary },
                                    }}
                                    variant="contained"
                                    label={"Edit"}
                                    onClick={() =>
                                        navigate(`/specialities/edit/${specialityDetails?.id}`)
                                    }
                                />
                            ) : (
                                <Button
                                    sx={{
                                        width: "200px",
                                        height: "55px",
                                        ":hover": { backgroundColor: colors.primary },
                                    }}
                                    variant="contained"
                                    label={"Activate"}
                                    onClick={() => setActivateModalVisible(true)}
                                />
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
            {activateModalVisible && (
                <ConfirmationDialog
                    visible={activateModalVisible}
                    message={`Are you sure you want to activate "${specialityDetails?.name}"?`}
                    onClose={() => setActivateModalVisible(false)}
                    onConfirm={handleActivate}
                    loading={isActivating}
                />
            )}
        </Box>
    );
}

export default SpecialityDetails;
